<mat-card class="send-email-card">
  <mat-card-content class="card-content">
    @if (isLoading()) {
      <div class="loading-container">
        <div class="spinner-wrapper">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ 'LOADING' | translate }}</span>
          </div>
        </div>
      </div>
    } @else {
      <form [formGroup]="emailForm" novalidate class="activity-content">
        <div class="recipient-fields">
          @if (connections.length > 0) {
            <glxy-form-field
              bottomSpacing="small"
              [showLabel]="false"
              class="detail-form-field"
              [prefixIcon]="'person'"
            >
              <mat-select
                [value]="selectedConnectionId()"
                (selectionChange)="selectedConnectionId.set($event.value)"
                [disabled]="isSending()"
              >
                @for (conn of connections; track conn.connectionId) {
                  <mat-option [value]="conn.connectionId">
                    {{ conn.label || ('CRM.SENDING_AS' | translate) }}
                    @if (conn.isDelegated) {
                      ({{ 'CRM.SHARED_CONNECTION' | translate }})
                    }
                  </mat-option>
                }
              </mat-select>
            </glxy-form-field>
          }

          <div class="recipient-row">
            <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.TO">
              <mat-chip-grid #toChipGrid>
                @for (email of toChips(); track email) {
                  <mat-chip-row
                    [removable]="canRemoveToChip()"
                    (removed)="removeToChip(email)"
                    (mousedown)="$event.preventDefault()"
                  >
                    {{ email }}
                    @if (canRemoveToChip()) {
                      <mat-icon matChipRemove>cancel</mat-icon>
                    }
                  </mat-chip-row>
                }
                <input
                  [matChipInputFor]="toChipGrid"
                  [placeholder]="toChips().length === 0 ? ('CRM.EMAIL' | translate) : ''"
                  readonly
                  (click)="openContactSelector('to')"
                />
              </mat-chip-grid>
            </glxy-form-field>
            <div class="cc-bcc-toggles">
              @if (!showCc()) {
                <button type="button" class="cc-bcc-toggle" (click)="toggleCc()" [disabled]="isSending()">
                  {{ 'CRM.CC' | translate }}
                </button>
              }
              @if (!showBcc()) {
                <button type="button" class="cc-bcc-toggle" (click)="toggleBcc()" [disabled]="isSending()">
                  {{ 'CRM.BCC' | translate }}
                </button>
              }
            </div>
          </div>
          @if (showErrors() && toChips().length === 0) {
            <glxy-error>
              {{ 'CRM.EMAIL_IS_REQUIRED' | translate }}
            </glxy-error>
          }

          @if (showCc()) {
            <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.CC">
              <mat-chip-grid #ccChipGrid>
                @for (email of ccChips(); track email) {
                  <mat-chip-row (removed)="removeCcChip(email)" (mousedown)="$event.preventDefault()">
                    {{ email }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                  </mat-chip-row>
                }
                <input
                  [formControl]="ccSearchControl"
                  [matChipInputFor]="ccChipGrid"
                  [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
                  [matChipInputAddOnBlur]="true"
                  (matChipInputTokenEnd)="addCcChip($event)"
                  [matAutocomplete]="ccAuto"
                  [placeholder]="ccChips().length === 0 ? ('CRM.CC' | translate) : ''"
                  type="email"
                  (click)="openContactSelector('cc')"
                />
              </mat-chip-grid>
              <mat-autocomplete #ccAuto="matAutocomplete" (optionSelected)="selectCcContact($event)">
                @for (contact of ccSearchResults(); track contact.id) {
                  <mat-option [value]="contact">
                    {{ contact.name }} ({{ contact.email }})
                  </mat-option>
                }
              </mat-autocomplete>
            </glxy-form-field>
          }

          @if (showBcc()) {
            <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" prefixText="CRM.BCC">
              <mat-chip-grid #bccChipGrid>
                @for (email of bccChips(); track email) {
                  <mat-chip-row (removed)="removeBccChip(email)" (mousedown)="$event.preventDefault()">
                    {{ email }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                  </mat-chip-row>
                }
                <input
                  [formControl]="bccSearchControl"
                  [matChipInputFor]="bccChipGrid"
                  [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
                  [matChipInputAddOnBlur]="true"
                  (matChipInputTokenEnd)="addBccChip($event)"
                  [matAutocomplete]="bccAuto"
                  [placeholder]="bccChips().length === 0 ? ('CRM.BCC' | translate) : ''"
                  type="email"
                  (click)="openContactSelector('bcc')"
                />
              </mat-chip-grid>
              <mat-autocomplete #bccAuto="matAutocomplete" (optionSelected)="selectBccContact($event)">
                @for (contact of bccSearchResults(); track contact.id) {
                  <mat-option [value]="contact">
                    {{ contact.name }} ({{ contact.email }})
                  </mat-option>
                }
              </mat-autocomplete>
            </glxy-form-field>
          }

          @if (recipientLimitError()) {
            <glxy-error class="recipient-limit-error">
              {{ 'CRM.TOO_MANY_RECIPIENTS' | translate }}
            </glxy-error>
          }

          <glxy-form-field bottomSpacing="small" [showLabel]="false" class="detail-form-field" [prefixIcon]="'subject'">
            <input
              #subjectInput
              formControlName="subject"
              type="text"
              matInput
              [placeholder]="'CRM.SUBJECT' | translate"
              [disabled]="isSending()"
            />
            @if (showErrors()) {
              @if (emailForm.controls.subject.hasError('required')) {
                <glxy-error>
                  {{ 'CRM.SUBJECT_IS_REQUIRED' | translate }}
                </glxy-error>
              }
            }
          </glxy-form-field>
        </div>

        <div class="editor-container">
          <glxy-form-row class="detail-form-row">
            <glxy-rich-text-editor
              class="rich-text-editor"
              elementId="send-email-rte"
              formControlName="content"
              [statusbar]="true"
              [enableThemes]="true"
              [allowResize]="true"
              [plugins]="DEFAULT_RICH_TEXT_PLUGINS"
              [toolbar]="DEFAULT_RICH_TEXT_TOOLBAR"
              [disabled]="isSending()"
            ></glxy-rich-text-editor>
            @if (showErrors()) {
              @if (emailForm.controls.content.hasError('required')) {
                <glxy-error>
                  {{ 'CRM.CONTENT_IS_REQUIRED' | translate }}
                </glxy-error>
              }
            }
          </glxy-form-row>
        </div>

        <div class="visibility-section">
          <glxy-form-field
            bottomSpacing="none"
            [showLabel]="false"
            class="detail-form-field visibility-field"
            [prefixIcon]="'visibility'"
          >
            <mat-select formControlName="visibility" [disabled]="isSending()">
              @for (option of visibilityOptions; track option.value) {
                <mat-option [value]="option.value">
                  {{ option.label | translate }}
                </mat-option>
              }
            </mat-select>
          </glxy-form-field>
          <span
            class="visibility-help-link"
            [glxyTooltip]="'CRM.VISIBILITY_TOOLTIP' | translate"
            [tooltipPositions]="tooltipPositions"
            >{{ 'CRM.WHATS_THIS' | translate }}</span
          >
        </div>
      </form>
    }
  </mat-card-content>

  @if (!isLoading()) {
    <mat-card-actions [align]="'end'" class="activity-form-actions">
      <button mat-flat-button color="primary" type="button" (click)="onSend()" [disabled]="isSending()">
        <glxy-button-loading-indicator [isLoading]="isSending()">
          {{ 'CRM.SEND' | translate }}
        </glxy-button-loading-indicator>
      </button>
      <button mat-stroked-button (click)="onCancel()" type="button" [disabled]="isSending()">
        {{ 'CRM.CANCEL' | translate }}
      </button>
    </mat-card-actions>
  }
</mat-card>
